import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  People as TherapistsIcon,
  SupervisorAccount as SupervisorIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { getAssignmentStats, getPlatformSettings } from '../../../services/api';

// Tab components (to be created)
import TherapistsTab from './TherapistsTab';
import SupervisorsTab from './SupervisorsTab';
import ClientAssignmentsTab from './ClientAssignmentsTab';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`staff-tabpanel-${index}`}
      aria-labelledby={`staff-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function ManageStaff({ token }) {
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Platform capacity defaults
  const [defaultMaxClients, setDefaultMaxClients] = useState(20);
  const [defaultMaxTherapists, setDefaultMaxTherapists] = useState(10);

  useEffect(() => {
    loadStats();
  }, [token, refreshTrigger]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [statsData, platformSettings] = await Promise.all([
        getAssignmentStats(token),
        getPlatformSettings(token)
      ]);

      setStats(statsData);

      // Load capacity defaults from platform settings
      if (platformSettings?.clientAssignmentSettings) {
        setDefaultMaxClients(platformSettings.clientAssignmentSettings.defaultTherapistMaxClients || 20);
        setDefaultMaxTherapists(platformSettings.clientAssignmentSettings.defaultSupervisorMaxTherapists || 10);
      }

      setError('');
    } catch (err) {
      console.error('Error loading assignment stats:', err);
      setError(err.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Staff Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Quick Stats Summary */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, flex: '1 1 200px', minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">
              Therapists
            </Typography>
            <Typography variant="h5">
              {stats.activeTherapists}/{stats.totalTherapists}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Active
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, flex: '1 1 200px', minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">
              Clinical Supervisors
            </Typography>
            <Typography variant="h5">
              {stats.activeSupervisors}/{stats.totalSupervisors}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Active
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, flex: '1 1 200px', minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">
              Clients
            </Typography>
            <Typography variant="h5">
              {stats.assignedClients}/{stats.totalClients}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Assigned
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, flex: '1 1 200px', minWidth: 200 }}>
            <Typography variant="caption" color="text.secondary">
              Unassigned Clients
            </Typography>
            <Typography variant="h5" color="warning.main">
              {stats.unassignedClients}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Need Assignment
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="staff management tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<TherapistsIcon />}
            iconPosition="start"
            label="Therapists"
            id="staff-tab-0"
            aria-controls="staff-tabpanel-0"
          />
          <Tab
            icon={<SupervisorIcon />}
            iconPosition="start"
            label="Clinical Supervisors"
            id="staff-tab-1"
            aria-controls="staff-tabpanel-1"
          />
          <Tab
            icon={<AssignmentIcon />}
            iconPosition="start"
            label="Client Assignments"
            id="staff-tab-2"
            aria-controls="staff-tabpanel-2"
          />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <TherapistsTab
            token={token}
            stats={stats}
            onRefresh={handleRefresh}
            defaultMaxClients={defaultMaxClients}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <SupervisorsTab
            token={token}
            stats={stats}
            onRefresh={handleRefresh}
            defaultMaxTherapists={defaultMaxTherapists}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <ClientAssignmentsTab
            token={token}
            stats={stats}
            onRefresh={handleRefresh}
          />
        </TabPanel>
      </Paper>
    </Box>
  );
}

export default ManageStaff;
